import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import { ShoppingCart, ChevronLeft, ChevronRight, Tag, Heart } from 'lucide-react';
import { Button } from './ui/button';
import OfferModal from './OfferModal';
import FrameSelector from './FrameSelector';
import ShareButtons from './ShareButtons';
import AskOnWhatsApp from './AskOnWhatsApp';
import { formatDimensions, formatUSD } from '../utils/format';
import { slugify } from '../utils/slug';
import { trackArtworkView } from '../utils/track';
import './Works.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Works = () => {
  const [selectedWork, setSelectedWork] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState('black');
  const { addToCart } = useCart();
  const { t, lang } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/artworks?category=works`, { cache: 'no-store' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        // Reorder so that favorited artworks appear first (preserves original order otherwise)
        const favs = JSON.parse(localStorage.getItem('favorites') || '[]');
        const sorted = [...list].sort((a, b) => {
          const aFav = favs.includes(a.id) ? 0 : 1;
          const bFav = favs.includes(b.id) ? 0 : 1;
          return aFav - bFav;
        });
        setArtworks(sorted);
      })
      .catch(() => setArtworks([]))
      .finally(() => setLoading(false));
  }, []);

  const handleAddToCart = (work) => {
    if (work.available) {
      addToCart(work, { frame: selectedFrame });
      setSelectedWork(null);
    }
  };

  const openModal = (work) => {
    setSelectedWork(work);
    setCurrentImageIndex(0);
    setSelectedFrame('black');
    trackArtworkView(work);
    // Reflect URL for sharing / SEO without re-rendering the page
    const slug = work.slug || slugify(work.title);
    if (slug && typeof window !== 'undefined') {
      window.history.pushState({ workModal: true }, '', `/works/${slug}`);
    }
  };

  const closeModal = () => {
    setSelectedWork(null);
    setCurrentImageIndex(0);
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/works/')) {
      // Undo the pushState from openModal so Back doesn't return here
      window.history.back();
    }
  };

  // Close modal on browser back button (URL will revert)
  useEffect(() => {
    const onPop = () => {
      if (!window.location.pathname.startsWith('/works/')) {
        setSelectedWork(null);
        setCurrentImageIndex(0);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const getImagesArray = (work) => {
    return [
      { url: work.image, label: t('label_full_artwork') },
      { url: work.detail, label: t('label_detail') },
      { url: work.scene, label: t('label_in_scene') }
    ];
  };

  const nextImage = () => {
    if (selectedWork) {
      const images = getImagesArray(selectedWork);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (selectedWork) {
      const images = getImagesArray(selectedWork);
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <section id="works" className="works-section">
      <div className="works-container">
        <div className="section-header">
          <h2 className="section-title">{t('works_title')}</h2>
          <p className="section-description">
            {t('works_description')}
          </p>
        </div>

        <div className="works-grid">
          {loading && artworks.length === 0 && (
            <p className="works-loading">{lang === 'es' ? 'Cargando obras…' : 'Loading works…'}</p>
          )}
          {!loading && artworks.length === 0 && (
            <p className="works-empty">{lang === 'es' ? 'Próximamente nuevas obras.' : 'New works coming soon.'}</p>
          )}
          {artworks.map((work, index) => (
            <div 
              key={work.id} 
              className="work-item"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openModal(work)}
              data-testid={`work-item-${work.id}`}
            >
              <div className="work-image-wrapper">
                <img 
                  src={work.image} 
                  alt={work.title}
                  className="work-image"
                />
                <div className={`work-status-badge ${work.available ? 'available' : 'sold'}`}>
                  {work.available ? t('badge_available') : t('badge_sold')}
                </div>
                <button 
                  className={`favorite-btn ${isFavorite(work.id) ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(work.id); }}
                  data-testid={`fav-${work.id}`}
                  aria-label="Save to favorites"
                >
                  <Heart size={18} fill={isFavorite(work.id) ? '#000' : 'none'} />
                </button>
                <div className="work-overlay">
                  <span className="work-view-text">{t('view_details')}</span>
                </div>
              </div>
              <div className="work-info">
                <h3 className="work-title" translate="no">{work.title}</h3>
                {work.price > 0 && (
                  <p className="work-price" translate="no">{formatUSD(work.price)} USD</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedWork && (
        <div className="work-modal" onClick={closeModal}>
          <div className="work-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal} data-testid="modal-close-btn">
              ×
            </button>
            <div className="modal-grid">
              <div className="modal-gallery">
                <div className="modal-image-container">
                  <img 
                    src={getImagesArray(selectedWork)[currentImageIndex].url} 
                    alt={selectedWork.title} 
                  />
                  <button className="gallery-nav prev" onClick={prevImage} data-testid="gallery-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button className="gallery-nav next" onClick={nextImage} data-testid="gallery-next">
                    <ChevronRight size={24} />
                  </button>
                </div>
                <div className="gallery-thumbnails">
                  {getImagesArray(selectedWork).map((img, idx) => (
                    <div 
                      key={idx}
                      className={`thumbnail ${currentImageIndex === idx ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(idx)}
                      data-testid={`thumbnail-${idx}`}
                    >
                      <img src={img.url} alt={img.label} />
                      <span className="thumbnail-label">{img.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-details">
                <div className={`modal-status ${selectedWork.available ? 'available' : 'sold'}`}>
                  {selectedWork.available ? t('badge_available') : t('badge_sold')}
                </div>
                <h3 translate="no">{selectedWork.title}</h3>
                <p className="modal-year">{selectedWork.year}</p>
                <p className="modal-medium">{selectedWork.medium}</p>
                <p className="modal-dimensions" translate="no">{formatDimensions(selectedWork.dimensions, lang)}</p>
                {selectedWork.available && (
                  <>
                    <p className="modal-price" translate="no">{formatUSD(selectedWork.price)} USD</p>
                    <ul className="modal-details-list">
                      <li className="detail-shipping">{t('detail_shipping')}</li>
                      <li>{t('detail_ready')}</li>
                      <li>{t('detail_signed')}</li>
                      <li>{t('detail_certificate')}</li>
                    </ul>
                    <FrameSelector value={selectedFrame} onChange={setSelectedFrame} testId="work-frame" />
                    <Button 
                      onClick={() => handleAddToCart(selectedWork)}
                      className="add-to-cart-button"
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart size={20} />
                      {t('add_to_cart')}
                    </Button>
                    <AskOnWhatsApp title={selectedWork.title} testId="ask-whatsapp-modal-btn" />
                    {selectedWork.price > 2500 && (
                      <button 
                        onClick={() => setShowOfferModal(true)}
                        className="make-offer-button"
                        data-testid="make-offer-btn"
                      >
                        <Tag size={18} />
                        {t('make_offer')}
                      </button>
                    )}
                  </>
                )}
                <ShareButtons artwork={selectedWork} />
              </div>
            </div>

            {/* You may also like section */}
            {(() => {
              const price = selectedWork.price || 0;
              const range = Math.max(price * 0.4, 500);
              const similar = artworks
                .filter(a => a.id !== selectedWork.id && a.available && Math.abs((a.price || 0) - price) <= range)
                .slice(0, 3);
              if (similar.length === 0) return null;
              return (
                <div className="you-may-like-section">
                  <h4 className="you-may-like-title">{t('you_may_like')}</h4>
                  <div className="you-may-like-grid">
                    {similar.map(item => (
                      <div 
                        key={item.id} 
                        className="suggestion-item"
                        onClick={() => {
                          setSelectedWork(item);
                          setCurrentImageIndex(0);
                          const slug = item.slug || slugify(item.title);
                          if (slug) window.history.replaceState({ workModal: true }, '', `/works/${slug}`);
                        }}
                        data-testid={`suggestion-${item.id}`}
                      >
                        <div className="suggestion-image">
                          <img src={item.image} alt={item.title} />
                        </div>
                        <div className="suggestion-info">
                          <p className="suggestion-title" translate="no">{item.title}</p>
                          <p className="suggestion-price" translate="no">{formatUSD(item.price)} USD</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showOfferModal && selectedWork && (
        <OfferModal 
          artwork={selectedWork}
          onClose={() => setShowOfferModal(false)}
        />
      )}
    </section>
  );
};

export default Works;
